import { createEnterKeyHandler } from 'utils/form'
import moment from 'moment'
import * as React from 'react'
import I18n from '../../../../core/i18n'
import styled from 'styled-components'
import * as constants from '../../../../static/constants'
import { Button, InputText, Select } from '../../../atoms'
import SmallDateRangePickerCustomStyle from 'static/smallDateRangePickerCustomStyle'
import { DateRangePicker } from 'react-dates'
import { Form } from 'components/molecules'
import Typography from 'components/atoms/_layouts/Typography'

interface IProps {
  currentPagePath: string
  csv_path?: string
  currentUserIsAgent?: boolean
  currentUserIsGuest?: boolean
}
interface IType {
  id: number | string
  name: string
}
interface IOption {
  value: string
  label: string
}
interface IFilter {
  'state[]': string
  reservation_no?: string
  time_scope: 'all' | 'upcoming' | 'past'
  reservation_type: 'all' | 'reservation' | 'direct_reservation'
  direction: 'asc' | 'desc'
  belongs_to: 'all' | 'me' | 'other'
}
// 予約状況： state[] ※これまでと同じ。ステータスを配列で受け取る。指定が無い場合(default)は全ステータス
// 催行状況：time_scope => all/upcoming/past　※指定が無い場合(default)はupcoming
// 予約区分：reservation_type => all/reservation/direct_reservation ※指定が無い場合(default)はall
// 並び順：sort, direction ※これまでと同じ。指定な無い場合は催行日古い順
// アサイン状況：belongs_to => all/me/other ※指定が無い場合(default)はall
const FIELDS = {
  'state[]': 'state[]',
  reservation_no: 'reservation_no',
  time_scope: 'time_scope',
  reservation_type: 'reservation_type',
  direction: 'direction',
  belongs_to: 'belongs_to',
}
const GUEST_FIELDS = {
  'state[]': 'state[]',
  time_scope: 'time_scope',
}
export const STATUS: IType[] = [
  {
    id: 'canceled',
    name: I18n.t('enums.reservation.workflow_state.canceled'),
  },
  {
    id: 'approved',
    name: I18n.t('enums.reservation.workflow_state.approved'),
  },
  {
    id: 'declined',
    name: I18n.t('enums.reservation.workflow_state.declined'),
  },
  {
    id: 'requested',
    name: I18n.t('enums.reservation.workflow_state.requested'),
  },
]

const STATUS_OPTIONS: IOption[] = [
  {
    value: '',
    label: I18n.t('generic.all'),
  },
  ...STATUS.map(state => ({
    value: state.id.toString(),
    label: state.name,
  })),
]

const ALL = {
  value: 'all',
  label: I18n.t('generic.all'),
}
const TIME_SCOPES = [
  ALL,
  {
    value: 'upcoming',
    label: I18n.t('reservation.show_upcoming_reservation'),
  },
  {
    value: 'past',
    label: I18n.t('reservation.show_passed_reservation'),
  },
]

const RESERVATION_TYPES = [
  ALL,
  {
    value: 'reservation',
    label: I18n.t('generic.reservations'),
  },
  {
    value: 'direct_reservation',
    label: I18n.t('generic.direct_reservation'),
  },
]

const BELONGS_TO = [
  ALL,
  {
    value: 'me',
    label: I18n.t('reservation.me'),
  },
  {
    value: 'other',
    label: I18n.t('generic.assigned'),
  },
]

const DIRECTION = [
  {
    value: 'desc',
    label: I18n.t('reservation.desc_day'),
  },
  {
    value: 'asc',
    label: I18n.t('reservation.asc_day'),
  },
]
// 必要になったらオプション、セレクト化する
const SORT = 'start_at'

const urlParams = new URLSearchParams(window.location.search)
const _start = urlParams.get('start')
const _end = urlParams.get('end')

const _today = moment()

export const ReservationIndexFilter: React.FC<IProps> = props => {
  const [start, setStart] = React.useState<moment.Moment>(_start ? moment(_start) : undefined)
  const [end, setEnd] = React.useState<moment.Moment>(_end ? moment(_end) : undefined)
  const [focusedInput, setFocusedInput] = React.useState(null)
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false)
  const [errors, setErrors] = React.useState({})
  const [filterValues, setFilterValues] = React.useState<IFilter | null>(null)
  const todayMoment = React.useMemo(() => moment(_today.format('YYYY-MM-DD')), [])

  const handleCalendarOutSideRange = React.useCallback(
    (calendarMoment: moment.Moment) => {
      if (filterValues?.time_scope === 'all') {
        return false
      }
      return filterValues?.time_scope === 'past'
        ? calendarMoment.isSameOrAfter(todayMoment)
        : calendarMoment.isBefore(todayMoment)
    },
    [filterValues?.time_scope]
  )

  const onDateRangePickerChange = (dates: {
    startDate: moment.Moment | null
    endDate: moment.Moment | null
  }) => {
    setStart(dates.startDate)
    setEnd(dates.endDate)
  }

  const changeFocusedInput = (changedFocusedInput: any) => {
    setFocusedInput(changedFocusedInput)
  }

  const applyFilter = React.useCallback(() => {
    if (!filterValues) {
      return
    }
    const newURLParam = new URLSearchParams()
    for (const key of Object.keys(filterValues)) {
      const value = filterValues[key]
      if (value) {
        newURLParam.set(key, value)
      }
    }

    if (start && end) {
      newURLParam.set('start', start.format(constants.DATE_FORMAT))
      newURLParam.set('end', end.format(constants.DATE_FORMAT))
    }

    location.href = `${props.currentPagePath}?${newURLParam.toString()}`
  }, [props, start, end, filterValues])

  const clearFilter = () => {
    location.href = `${props.currentPagePath}`
  }

  const handleUpdateForm = React.useCallback((updatedErrors, updatedIsSubmitEnabled, values) => {
    setErrors(updatedErrors)
    setIsSubmitEnabled(updatedIsSubmitEnabled)
    if (values) {
      setFilterValues(values)
    }
  }, [])

  const submit = async (_, values: any) => {
    setFilterValues(values)
  }
  const onChangeTimeScope = React.useCallback(
    e => {
      const needChange = e.target.value !== 'all' && e.target.value !== urlParams.get('time_scope')
      if (!needChange && _start && _end) {
        setStart(moment(_start))
        setEnd(moment(_end))
      } else if (start && end) {
        setStart(undefined)
        setEnd(undefined)
      }
    },
    [start, end]
  )

  const handleSearchKeyDown = createEnterKeyHandler(applyFilter)

  return (
    <div className="ReservationIndex_Header">
      <Form
        fields={props.currentUserIsGuest ? GUEST_FIELDS : FIELDS}
        handleUpdateForm={handleUpdateForm}
        handleSubmit={submit}
      >
        <FiltersWrap>
          <div>
            <Typography type="subtitle">催行状況</Typography>
            <Select
              name="time_scope"
              options={TIME_SCOPES}
              defaultValue={urlParams.get('time_scope') ?? 'upcoming'}
              onChangeHandler={onChangeTimeScope}
            />
          </div>

          <div>
            <Typography type="subtitle">予約状況</Typography>
            <Select
              name="state[]"
              options={STATUS_OPTIONS}
              defaultValue={urlParams.get('state[]') ?? 'all'}
            />
          </div>
          {!props.currentUserIsGuest && (
            <>
              <div>
                <Typography type="subtitle">予約番号</Typography>
                <InputText
                  onKeyDownHandler={handleSearchKeyDown}
                  name="reservation_no"
                  defaultValue={urlParams.get('reservation_no') ?? ''}
                  placeholder="RE-XXXXXXXXXX"
                />
              </div>
              <div>
                <Typography type="subtitle">予約区分</Typography>
                <Select
                  name="reservation_type"
                  options={RESERVATION_TYPES}
                  defaultValue={urlParams.get('reservation_type') ?? 'all'}
                />
              </div>
              {!props.currentUserIsAgent ? (
                <div>
                  <Typography type="subtitle">アサイン状況</Typography>
                  <Select
                    name="belongs_to"
                    options={BELONGS_TO}
                    defaultValue={urlParams.get('belongs_to') ?? 'all'}
                  />
                </div>
              ) : (
                <input name="belongs_to" value="all" readOnly={true} style={{ display: 'none' }} />
              )}
              <div>
                <Typography type="subtitle">並び順</Typography>
                <Select
                  name="direction"
                  options={DIRECTION}
                  defaultValue={urlParams.get('direction') ?? 'desc'}
                />
              </div>
              <div>
                <Typography type="subtitle">ツアー開始日</Typography>
                <SmallDateRangePickerCustomStyle />
                <DateRangePicker
                  startDate={start}
                  startDateId="start_date"
                  endDate={end}
                  endDateId="end_date"
                  onDatesChange={onDateRangePickerChange}
                  focusedInput={focusedInput}
                  onFocusChange={(currentFocusedInput: string | null) =>
                    changeFocusedInput(currentFocusedInput)
                  }
                  numberOfMonths={1}
                  isOutsideRange={handleCalendarOutSideRange}
                  hideKeyboardShortcutsPanel={true}
                  displayFormat={constants.DATE_FORMAT}
                  minimumNights={0}
                />
              </div>
            </>
          )}
          <div style={{ paddingTop: 18 }}>
            <Button
              backgroundColor={constants.COLORS.secondary}
              textColor={constants.COLORS.white}
              small={true}
              handleClick={applyFilter}
              disabled={!filterValues}
            >
              {I18n.t('generic.filter')}
            </Button>
            <Button small={true} handleClick={clearFilter}>
              {I18n.t('generic.clear')}
            </Button>
          </div>
        </FiltersWrap>
      </Form>
    </div>
  )
}

const FiltersWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`
